import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Paper,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert
} from '@mui/material';
import { StarOutline as StarIcon, CheckCircle as CheckIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

// Premium package data
const premiumPackage = {
  name: 'Persoonlijk Ontwikkelingsplan',
  price: 750,
  duration: '1 jaar',
  sessions: 10,
  description: 'Complete begeleiding bij het maken en implementeren van je persoonlijke ontwikkelingsplan',
  features: [
    'Onbeperkte toegang tot alle premium materialen gedurende 1 jaar',
    '10 individuele coaching sessies',
    'Persoonlijk ontwikkelplan op maat',
    'Voortgangsevaluaties',
    'E-mail ondersteuning tijdens het hele traject'
  ]
};

const PremiumActivation = () => {
  const { isPremiumUser, currentUser } = useAuth();
  const navigate = useNavigate();
  const [redirecting, setRedirecting] = useState(false);
  const [countdown, setCountdown] = useState(5);
  
  // Check if already premium
  const alreadyPremium = isPremiumUser();
  
  useEffect(() => {
    if (alreadyPremium) {
      // If already premium, redirect to premium dashboard after a short delay
      const timer = setTimeout(() => {
        navigate('/mijn-content');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [alreadyPremium, navigate]);
  
  useEffect(() => {
    // Start countdown if redirecting
    if (redirecting && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (redirecting && countdown === 0) {
      navigateToContactForm();
    }
  }, [redirecting, countdown]);

  const handleActivateRequest = () => {
    setRedirecting(true);
  };

  const navigateToContactForm = () => {
    // Get user email if available
    const userEmail = currentUser?.email || '';
    // Safely access displayName which might not exist on the User type
    const userName = currentUser ? (currentUser as any).displayName || '' : '';
    
    // Navigate to contact with premium request prefilled
    navigate('/contact', { 
      state: { 
        prefill: {
          name: userName,
          email: userEmail,
          subject: 'Premium Toegang Aanvraag - Persoonlijk Ontwikkelingsplan',
          message: `Ik zou graag het Premium Pakket "Persoonlijk Ontwikkelingsplan" willen aanschaffen voor €750,- inclusief 10 coaching sessies en 1 jaar toegang tot alle premium materialen. Graag neem ik contact op voor de betaling en planning van de eerste afspraak.`
        }
      }
    });
  };
  
  if (alreadyPremium) {
    return (
      <Box sx={{ py: 12, bgcolor: 'background.default', minHeight: 'calc(100vh - 132px)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <Alert severity="success" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
            <CheckIcon sx={{ mr: 1 }} />
            <Typography variant="h6">
              Je hebt al premium toegang
            </Typography>
          </Alert>
          <Typography variant="body1" paragraph>
            Je wordt doorgestuurd naar de premium content...
          </Typography>
          <CircularProgress sx={{ mt: 2 }} />
        </Container>
      </Box>
    );
  }
  
  return (
    <Box sx={{ py: 12, bgcolor: 'background.default', minHeight: 'calc(100vh - 132px)' }}>
      <Container maxWidth="md">
        <Typography variant="h2" component="h1" align="center" gutterBottom>
          Premium Pakket Activeren
        </Typography>
        <Typography variant="body1" align="center" sx={{ mb: 6 }}>
          Upgrade je ervaring met het premium pakket voor coaching en persoonlijke ontwikkeling
        </Typography>
        
        <Paper sx={{ p: 4, mb: 4, border: '1px solid', borderColor: 'secondary.main' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Typography variant="h4" component="h2" color="secondary.main">
              {premiumPackage.name}
            </Typography>
            <Chip 
              color="secondary" 
              label={`€${premiumPackage.price},-`} 
              sx={{ fontSize: '1.2rem', fontWeight: 'bold', py: 2, px: 1 }} 
            />
          </Box>
          
          <Typography variant="body1" paragraph>
            {premiumPackage.description}
          </Typography>
          
          <Divider sx={{ my: 3 }} />
          
          <Typography variant="h6" gutterBottom>
            Dit pakket omvat:
          </Typography>
          
          <List>
            {premiumPackage.features && Array.isArray(premiumPackage.features) ? 
              premiumPackage.features.map((feature, index) => (
                <ListItem key={index} sx={{ py: 1 }}>
                  <ListItemIcon>
                    <StarIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText primary={feature || ''} />
                </ListItem>
              )) : null
            }
          </List>
          
          <Divider sx={{ my: 3 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Box>
              <Typography variant="subtitle1" component="span" sx={{ mr: 1 }}>Looptijd:</Typography>
              <Typography variant="h6" component="span">{premiumPackage.duration}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle1" component="span" sx={{ mr: 1 }}>Sessies:</Typography>
              <Typography variant="h6" component="span">{premiumPackage.sessions}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle1" component="span" sx={{ mr: 1 }}>Prijs:</Typography>
              <Typography variant="h6" component="span" fontWeight="bold">€{premiumPackage.price},-</Typography>
            </Box>
          </Box>
          
          {redirecting ? (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="h6">
                Je wordt doorgestuurd naar het contactformulier in {countdown} seconden...
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Vul het contactformulier in om je aanvraag voor het premium pakket af te ronden
              </Typography>
            </Box>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              size="large"
              fullWidth
              onClick={handleActivateRequest}
              sx={{ py: 1.5, fontSize: '1.1rem' }}
            >
              Aanvragen Premium Pakket
            </Button>
          )}
        </Paper>
        
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Hoe werkt het?
          </Typography>
          <Typography variant="body2" paragraph>
            1. Klik op de knop "Aanvragen Premium Pakket" hierboven
          </Typography>
          <Typography variant="body2" paragraph>
            2. Vul het contactformulier in met je gegevens
          </Typography>
          <Typography variant="body2" paragraph>
            3. De coach neemt binnen 24 uur contact met je op om de betaling en planning te bespreken
          </Typography>
          <Typography variant="body2">
            4. Na betaling krijg je direct toegang tot alle premium materialen en kun je je coaching sessies plannen
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default PremiumActivation; 